// On-Track Technologies — a Gantt any custom page in this fork can drop in.
//
// Modelled on the production-plan Gantt in another client fork (the client asked
// for that behaviour), but over the generic row shape these pages already
// carry, so it is not tied to one domain:
//
//   rows = [{ key, summary, status, start, due, done, group, level, items }]
//
// `rowId` is optional and only matters to a TREE: the same work item can sit
// under two parents (the Investment Projects structure does exactly that),
// and two rows carrying the same `key` are still two rows. Pass the row's
// path as `rowId` there; `key` stays the work item's key, which is what a
// click opens.
//
//   · a bar from start → due; ONE date draws a milestone diamond, because a
//     zero-length bar reads as missing data when it is not;
//   · anything past its due date and not done gets a red tail from the due
//     date to today — the overrun is drawn, not just coloured;
//   · today is a line, labelled;
//   · rows nest by `group` and indent by `level`, and a group header carries
//     the span of everything under it;
//   · the grain switches Days / Weeks / Months / Quarters / Years
//     (standard 1.1), and the choice sticks per page.
//
// Clicking a bar opens that work item; clicking a group header opens the
// whole group in search (standard 10).

import { useEffect, useMemo, useRef, useState } from 'react';
import { FONT } from '../high-level-finance/util';
import { INTERVALS, bucketOf, daysBetween, labelOf, pickFor } from './interval';

const pad = (n) => String(n).padStart(2, '0');
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const addDays = (isoDate, n) => {
  const [y, m, d] = String(isoDate).slice(0, 10).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
};

// px per day at each grain — the same idea as that production-plan chart's zoom table.
const PX = { day: 26, week: 7, month: 2.6, quarter: 1.1, year: 0.42 };
const LABEL_W = 300;

export default function Gantt({
  rows, ui, S, narrow, onOpenItem, onOpenGroup, storeKey = 'gantt',
  height = 420, title, subtitle, interval, onInterval,
  // The label pane is the caller's when it wants to be. Passing `renderLabel`
  // turns the left-hand side into THEIR grid — which is how the timeline
  // becomes part of a structure instead of a chart sitting next to one.
  labelWidth, labelHeader, renderLabel, right, rowHeight,
}) {
  const today = todayIso();
  const [localGrain, setLocalGrain] = useState(() => {
    try { return localStorage.getItem(`ott.${storeKey}.grain`) || ''; } catch { return ''; }
  });
  const dates = useMemo(() => rows.flatMap((r) => [r.start, r.due]).filter(Boolean).sort(), [rows]);
  const span = dates.length ? daysBetween(dates[0], dates[dates.length - 1]) : 0;
  const grain = interval || localGrain || pickFor(span || 0);
  const setGrain = (g) => {
    if (onInterval) onInterval(g);
    setLocalGrain(g);
    try { localStorage.setItem(`ott.${storeKey}.grain`, g); } catch { /* storage may be blocked */ }
  };

  const win = useMemo(() => {
    const lo = dates.length ? (dates[0] < today ? dates[0] : today) : today;
    const hi = dates.length ? (dates[dates.length - 1] > today ? dates[dates.length - 1] : today) : today;
    const from = addDays(lo, -7);
    const to = addDays(hi, 14);
    return { from, to, days: Math.max(1, (daysBetween(from, to) || 0) + 1) };
  }, [dates, today]);

  const px = PX[grain] || PX.month;
  const chartW = Math.max(240, win.days * px);
  const scroller = useRef(null);
  // Open on today rather than at the far left of history.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const x = (daysBetween(win.from, today) || 0) * px;
    el.scrollLeft = Math.max(0, x - el.clientWidth * 0.3);
  }, [win.from, px, today]);

  // The column ticks at this grain.
  const ticks = useMemo(() => {
    const out = []; let cur = win.from; let guard = 0;
    while (cur <= win.to && guard < 3000) {
      const k = bucketOf(cur, grain);
      if (!out.length || out[out.length - 1].key !== k) out.push({ key: k, day: cur });
      cur = addDays(cur, grain === 'day' ? 1 : grain === 'week' ? 7 : grain === 'month' ? 28 : grain === 'quarter' ? 80 : 300);
      guard += 1;
    }
    return out.map((t, i) => ({
      ...t,
      x: (daysBetween(win.from, t.day) || 0) * px,
      w: ((daysBetween(t.day, out[i + 1] ? out[i + 1].day : win.to) || 1)) * px,
    }));
  }, [win, grain, px]);

  const barOf = (r) => {
    const a = r.start; const b = r.due;
    if (!a && !b) return null;
    if (a && b) {
      const [s, e] = a <= b ? [a, b] : [b, a];
      return { kind: 'bar', x: (daysBetween(win.from, s) || 0) * px, w: Math.max(px, ((daysBetween(s, e) || 0) + 1) * px), from: s, to: e, inverted: a > b };
    }
    const d = b || a;
    return { kind: 'dot', x: (daysBetween(win.from, d) || 0) * px, w: px, from: d, to: d, onlyStart: !b };
  };
  const tone = (r) => {
    if (r.done) return '#4cc38a';
    if (r.due && r.due < today) return '#dc2626';
    return '#1868db';
  };

  const todayX = (daysBetween(win.from, today) || 0) * px;
  const rowH = rowHeight || 26;
  const labelW = labelWidth || (narrow ? 160 : LABEL_W);

  return (
    <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', padding: '10px 14px 6px' }}>
        {/* An embedded Gantt is inside someone else's card, which already
            carries the heading — title="" says so and leaves it out. */}
        {title === '' && !subtitle ? null : (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{title === undefined ? 'Gantt' : title}</div>
            {subtitle && <div style={{ ...S.tiny, marginTop: 2 }}>{subtitle}</div>}
          </div>
        )}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          {right}
          <span style={{ ...S.tiny, marginRight: 4, marginLeft: right ? 10 : 0 }}>Interval</span>
          {INTERVALS.map((iv) => (
            <button key={iv.id} onClick={() => setGrain(iv.id)} style={{ ...S.pill(grain === iv.id), padding: '3px 9px', fontSize: 11.5 }}>{iv.label}</button>
          ))}
        </span>
      </div>
      {rows.length === 0 ? (
        <div style={{ ...S.tiny, padding: '24px 14px', textAlign: 'center' }}>Nothing with a date in the current filters.</div>
      ) : (
        <div style={{ display: 'flex', borderTop: `1px solid ${ui.border}`, maxHeight: height, overflowY: 'auto' }}>
          <div style={{ flex: `0 0 ${labelW}px`, width: labelW, borderRight: `1px solid ${ui.border}`, background: ui.card, position: 'sticky', left: 0, zIndex: 2 }}>
            <div style={{ height: 30, borderBottom: `1px solid ${ui.border}`, display: 'flex', alignItems: 'center', padding: renderLabel ? 0 : '0 10px', ...S.tiny, fontWeight: 700 }}>
              {labelHeader || 'Work'}
            </div>
            {rows.map((r) => (renderLabel ? (
              <div key={r.rowId || r.key || r.summary} style={{ height: rowH, borderBottom: `1px solid ${ui.border}`, background: r.isGroup ? ui.surface : 'transparent', overflow: 'hidden' }}>
                {renderLabel(r)}
              </div>
            ) : (
              <div key={r.rowId || r.key || r.summary} title={r.summary}
                style={{ height: rowH, display: 'flex', alignItems: 'center', gap: 6, padding: `0 10px 0 ${10 + (r.level || 0) * 14}px`,
                         borderBottom: `1px solid ${ui.border}`, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                         fontWeight: r.isGroup ? 800 : 400, background: r.isGroup ? ui.surface : 'transparent', cursor: 'pointer' }}
                onClick={() => (r.isGroup ? onOpenGroup && onOpenGroup(r) : onOpenItem && onOpenItem(r))}>
                {!r.isGroup && r.key && <span style={{ color: ui.blue, fontWeight: 700 }}>{r.key}</span>}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.summary}</span>
                {r.isGroup && r.count != null && <span style={{ ...S.tiny, marginLeft: 'auto' }}>{r.count}</span>}
              </div>
            )))}
          </div>
          <div ref={scroller} style={{ flex: '1 1 auto', overflowX: 'auto' }}>
            <div style={{ width: chartW, position: 'relative' }}>
              <div style={{ height: 30, borderBottom: `1px solid ${ui.border}`, position: 'relative' }}>
                {ticks.map((t) => (
                  <span key={t.key} style={{ position: 'absolute', left: t.x, width: t.w, top: 0, height: 30, borderLeft: `1px solid ${ui.border}`,
                                             fontSize: 10, color: ui.muted, fontFamily: FONT, display: 'flex', alignItems: 'center', paddingLeft: 4, overflow: 'hidden', whiteSpace: 'nowrap' }}>
                    {labelOf(t.key, grain)}
                  </span>
                ))}
              </div>
              {rows.map((r) => {
                const b = barOf(r);
                const late = !r.done && r.due && r.due < today;
                const c = tone(r);
                return (
                  <div key={r.rowId || r.key || r.summary} style={{ height: rowH, borderBottom: `1px solid ${ui.border}`, position: 'relative',
                                                         background: r.isGroup ? ui.surface : 'transparent' }}>
                    {ticks.map((t) => <span key={t.key} style={{ position: 'absolute', left: t.x, top: 0, bottom: 0, borderLeft: `1px solid ${ui.border}`, opacity: 0.6 }} />)}
                    {late && b && (
                      <span style={{ position: 'absolute', left: b.x + b.w, width: Math.max(0, todayX - b.x - b.w), top: rowH / 2 - 1, height: 2, background: '#dc2626', opacity: 0.55 }} />
                    )}
                    {b && b.kind === 'bar' && (
                      <span title={`${r.summary}\n${b.from} → ${b.to}${r.status ? `\n${r.status}` : ''}${late ? '\nPAST ITS DUE DATE' : ''}`}
                        onClick={() => (r.isGroup ? onOpenGroup && onOpenGroup(r) : onOpenItem && onOpenItem(r))}
                        style={{ position: 'absolute', left: b.x, width: b.w, top: (rowH - (r.isGroup ? 10 : 13)) / 2, height: r.isGroup ? 10 : 13,
                                 background: r.isGroup ? `${c}55` : c, border: `1px solid ${c}`, borderRadius: 3, cursor: 'pointer' }} />
                    )}
                    {b && b.kind === 'dot' && (
                      <span title={`${r.summary}\n${b.onlyStart ? 'Starts' : 'Due'} ${b.from} (no ${b.onlyStart ? 'due' : 'start'} date)${late ? '\nPAST ITS DUE DATE' : ''}`}
                        onClick={() => (r.isGroup ? onOpenGroup && onOpenGroup(r) : onOpenItem && onOpenItem(r))}
                        style={{ position: 'absolute', left: b.x - 5, top: rowH / 2 - 5, width: 10, height: 10, background: c, transform: 'rotate(45deg)', cursor: 'pointer' }} />
                    )}
                  </div>
                );
              })}
              <span style={{ position: 'absolute', left: todayX, top: 0, bottom: 0, width: 2, background: '#f5a623', opacity: 0.9, pointerEvents: 'none' }} />
              <span style={{ position: 'absolute', left: todayX + 4, top: 6, fontSize: 10, fontWeight: 800, color: '#f5a623', fontFamily: FONT, pointerEvents: 'none' }}>TODAY</span>
            </div>
          </div>
        </div>
      )}
      <div data-pdf-footer style={{ ...S.tiny, padding: '6px 14px', display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center', borderTop: `1px solid ${ui.border}` }}>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#1868db', borderRadius: 2, marginRight: 5 }} />On the plan</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#dc2626', borderRadius: 2, marginRight: 5 }} />Past its due date</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#4cc38a', borderRadius: 2, marginRight: 5 }} />Done</span>
        <span>◆ one date only</span>
      </div>
    </div>
  );
}
